import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import Layout from "../../../../component/layout";
import { toastify } from "../../../../../../utils/toast";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../../../app/z-store/store";

const POWER_ANALOG_ADD_URL = "/api/app/power_meter_management/controllers/power_meters/add_analog_meter";
const POWER_METERS_PAGE = "/facility/utility_management/meter_management/power_meters";

const AddAnalogPowerMeter = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const [meterNumber, setMeterNumber] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [currentReading, setCurrentReading] = useState(0);
  const [meterType, setMeterType] = useState("2 phase");
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);

  const formatCustomerName = (customer) => {
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`;
    } else if (customer.fullName) {
      return customer.fullName;
    } else if (customer.name) {
      return customer.name;
    }
    return "Unnamed Customer";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedFacilityId) {
        toastify("No facility selected", "error");
        return;
      }
      try {
        setIsLoading(true);
        const [customersRes, unitsRes] = await Promise.all([
          makeRequest2(
            `/api/app/customer_management/get_customers/${selectedFacilityId}`,
            "GET"
          ),
          makeRequest2(
            `/api/app/unit_management/get_facility_units/${selectedFacilityId}`,
            "GET"
          )
        ]);

        if (customersRes.success) {
          const customersData =
            customersRes.data?.customers || customersRes.data || [];
          setCustomers(customersData);
        }
        if (unitsRes.success) {
          const unitsData = unitsRes.data?.units || unitsRes.data || [];
          setUnits(unitsData);
        }
      } catch (error) {
        toastify("Error loading data", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedFacilityId]);

  const handleSubmit = async () => {
    if (!selectedFacilityId) {
      toastify("No facility selected", "error");
      return;
    }
    try {
      if (!meterNumber.trim()) {
        toastify("Meter Number is required", "error");
        return;
      }
      if (!unitId) {
        toastify("Unit is required", "error");
        return;
      }
      setIsSubmitting(true);

      const selectedUnit = units.find((unit) => unit._id === unitId);
      const selectedCustomer = customers.find((cust) => cust._id === customerId);

      // Analog POWER meters are saved by the power meter service (with the customer's
      // postpaid account when one is chosen); they never go through the water meters.
      const analogMeterData = {
        facilityId: selectedFacilityId,
        meterSerialNumber: meterNumber.trim(),
        type: meterType,
        status: "installed",
        meterReading: Number(currentReading) || 0,
        unitId,
        unitName: selectedUnit?.name || selectedUnit?.unitNumber || "",
        customerId: customerId || undefined,
        customerName: selectedCustomer ? formatCustomerName(selectedCustomer) : undefined,
        phoneNumber: selectedCustomer?.phoneNumber || undefined,
        email: selectedCustomer?.email || undefined
      };

      const meterResponse = await makeRequest2(
        POWER_ANALOG_ADD_URL,
        "POST",
        analogMeterData
      );

      if (!meterResponse.success) {
        toastify(meterResponse.error || "Failed to add analog power meter", "error");
        setIsSubmitting(false);
        return;
      }

      toastify(
        customerId
          ? "Analog power meter and customer account added successfully"
          : "Analog power meter added successfully",
        "success"
      );

      handleClear();
      navigate(`${POWER_METERS_PAGE}?tab=analog-power-meters`);
    } catch (error) {
      toastify(
        "An unexpected error occurred: " +
        (error.message || "Unknown error"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setMeterNumber("");
    setCustomerId("");
    setUnitId("");
    setCurrentReading(0);
    setMeterType("2 phase");
  };

  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="text-center p-5">
          Please select a facility first
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center p-5">Loading data...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`${POWER_METERS_PAGE}?tab=analog-power-meters`}>
                    Analog Power Meters
                  </Link>
                </li>
                <li className="breadcrumb-item">Add Analog Meter</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to={`${POWER_METERS_PAGE}?tab=analog-power-meters`}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <h5>Add Analog Power Meter</h5>
            </div>
            <div className="card-body">
              <div className="container">
                <div className="row">
                  <div className="col-sm-6">
                    <label className="form-label">
                      Meter Number<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={meterNumber}
                      onChange={(e) => setMeterNumber(e.target.value)}
                      placeholder="Enter meter number"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label">
                      Meter Type<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={meterType}
                      onChange={(e) => setMeterType(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="2 phase">2 Phase</option>
                      <option value="3 phase">3 Phase</option>
                    </select>
                  </div>

                  <div className="col-sm-6">
                    <label className="form-label">Customer (Optional)</label>
                    <select
                      className="form-control"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Customer (Optional)</option>
                      {customers.map((customer) => (
                        <option key={customer._id} value={customer._id}>
                          {formatCustomerName(customer)}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      A customer gets a postpaid power account for billing.
                    </small>
                  </div>

                  <div className="col-sm-6">
                    <label className="form-label">
                      Unit<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={unitId}
                      onChange={(e) => setUnitId(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Unit</option>
                      {units.map((unit) => (
                        <option key={unit._id} value={unit._id}>
                          {unit.unitNumber || unit.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-sm-6 mb-3">
                    <label className="form-label">Current Reading</label>
                    <input
                      type="number"
                      className="form-control"
                      value={currentReading}
                      onChange={(e) => setCurrentReading(e.target.value)}
                      step="0.01"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={isSubmitting}
                >
                  Clear
                </Button>
                &nbsp;
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddAnalogPowerMeter;